import React, { useState, useRef, useEffect } from 'react';
import './SearchableMultiSelectDropdown.css';

/**
 * SearchableMultiSelectDropdown is a reusable component that renders a searchable dropdown with multiple selection capability.
 * @param {array} options - Array of options with {key, value, text} format
 * @param {array} selectedValues - Array of selected values
 * @param {function} onChange - Callback function when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} loading - Loading state
 * @param {string} className - Additional CSS class
 */

const SearchableMultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Search and select...",
  loading = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term and sort by selected status
  useEffect(() => {
    let filtered;
    if (searchTerm.trim() === "") {
      filtered = options;
    } else {
      filtered = options.filter(option =>
        option.text?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort: selected users first, then unselected users (both alphabetically)
    const sorted = [...filtered].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);

      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Both selected or both unselected - sort alphabetically by name
      const aName = a.text.split(' (')[0].toLowerCase();
      const bName = b.text.split(' (')[0].toLowerCase();
      return aName.localeCompare(bName);
    });

    setFilteredOptions(sorted);
  }, [searchTerm, options, selectedValues]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleSelectOption = (optionValue) => {
    let newSelectedValues;
    if (selectedValues.includes(optionValue)) {
      // Remove if already selected
      newSelectedValues = selectedValues.filter(val => val !== optionValue);
    } else {
      // Add if not selected
      newSelectedValues = [...selectedValues, optionValue];
    }
    onChange(newSelectedValues);
  };

  const handleRemoveTag = (optionValue, event) => {
    event.stopPropagation();
    const newSelectedValues = selectedValues.filter(val => val !== optionValue);
    onChange(newSelectedValues);
  };

  const getSelectedOptions = () => {
    return options.filter(option => selectedValues.includes(option.value));
  };

  const getDisplayText = () => {
    const selectedCount = selectedValues.length;
    if (selectedCount === 0) {
      return placeholder;
    }
    return `${selectedCount} selected`;
  };

  return (
    <div ref={dropdownRef} className={`searchable-multi-select ${className}`}>
      {/* Dropdown Trigger */}
      <div
        className={`dropdown-trigger ${isOpen ? 'active' : ''}`}
        onClick={handleToggleDropdown}
      >
        <div className="dropdown-display">
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              className="dropdown-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="dropdown-text">{getDisplayText()}</span>
          )}
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="dropdown-empty">No results found</div>
          ) : (
            <div className="dropdown-options">
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const displayName = option.text.split(' (')[0]; // Extract name without email
                return (
                  <div
                    key={option.key || option.value}
                    className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectOption(option.value)}
                  >
                    <span className="option-text">{displayName}</span>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="option-checkbox"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Selected Tags Display */}
      {selectedValues.length > 0 && !isOpen && (
        <div className="selected-tags">
          {getSelectedOptions().map((option) => {
            const userName = option.text.split(' (')[0];
            return (
              <div key={option.value} className="tag">
                <span className="tag-text">{userName}</span>
                <img
                  src="/images/redClose.svg"
                  alt="Remove"
                  className="tag-remove"
                  onClick={(e) => handleRemoveTag(option.value, e)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelectDropdown;
